//latest updated with transaction and wallet balance


import Header from "./Header";

import PaystackPaymentForm from "../../components/paystack";
import { useState, useEffect } from "react";
import { FaBell, FaChartBar, FaChartLine, FaExchangeAlt, FaLightbulb, FaPhoneVolume, FaTv, FaWallet, FaWifi, FaEye, FaTrash } from "react-icons/fa";
import { Link } from "react-router-dom";
import BuyAirtime from "./BuyAirtime";
import BuyData from "./BuyData";
import Modal from "../../components/modal";
import { db, auth } from "../../firebase/firebaseConfig"; // Ensure correct Firebase import paths
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, collection, getDocs, deleteDoc } from "firebase/firestore";
import CableSubscriptionForm from "./BuyTVSub";
import PayElectricityBill from "./BuyE-BillSub";
import TransactionReceipt from "./TransactionReceipt";

const Dashboard = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeComponent, setActiveComponent] = useState(null);
    const [walletBalance, setWalletBalance] = useState(0);
    const [totalTransactions, setTotalTransactions] = useState(0);
    const [notifications, setNotifications] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [user, setUser] = useState(null);
    const [userRole, setUserRole] = useState('user');
    const [upgrading, setUpgrading] = useState(false);

    const openModal = (component) => {
        setActiveComponent(() => component);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setActiveComponent(null);
    };

    const toggleMenu = () => {
        setIsOpen(!isOpen);
    };

    const handleSingleDelete = async (id) => {
        const confirmed = window.confirm("Are you sure you want to delete this transaction?");
        if (!confirmed) return;

        try {
            await deleteDoc(doc(db, `users/${user.uid}/transactions`, id));
            setTransactions((prev) => prev.filter((transaction) => transaction.id !== id));
        } catch (err) {
            console.error("Error deleting transaction:", err);
        }
    };

    const handleViewTransaction = (transaction) => {
        openModal(() => <TransactionReceipt transaction={transaction} />);
    };

    const getStatusColor = (status) => {
        if (!status) return 'text-gray-500 bg-gray-100';
        const s = String(status).toLowerCase();
        if (s.includes('success')) return 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400';
        if (s.includes('fail') || s.includes('error')) return 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400';
        if (s.includes('pend')) return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400';
        return 'text-gray-600 bg-gray-100 dark:bg-gray-800 dark:text-gray-300';
    };

    const handleUpgrade = async () => {
        if (!user) return;
        const confirmed = window.confirm(`Are you sure you want to upgrade your account to Reseller? This will deduct the one-time upgrade fee from your wallet.`);
        if (!confirmed) return;
        
        setUpgrading(true);
        try {
            const idToken = await user.getIdToken();
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
            const res = await fetch(`${apiUrl}/api/v1/users/upgrade`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${idToken}` }
            });
            if (res.ok) {
                alert("Account upgraded successfully! You are now a Reseller.");
                window.location.reload();
            } else {
                const errData = await res.json().catch(() => ({}));
                alert(`Upgrade failed: ${errData.detail || 'Unknown error'}`);
            }
        } catch (err) {
            console.error(err);
            alert("An error occurred during upgrade.");
        } finally {
            setUpgrading(false);
        }
    };


    useEffect(() => {
        const fetchDashboardData = async (uid) => {
            // Fetch Wallet Balance
            try {
                const userDocRef = doc(db, "users", uid);
                const userDoc = await getDoc(userDocRef);
                if (userDoc.exists()) {
                    const userData = userDoc.data();
                    setWalletBalance(userData.wallet?.currentBalance || 0);
                    setUserRole(userData.role || 'user');
                }
            } catch (error) {
                console.error("Error fetching wallet data:", error);
            }

            // Fetch Transactions
            try {
                const transactionsRef = collection(db, `users/${uid}/transactions`);
                const transactionsSnapshot = await getDocs(transactionsRef);

                let total = 0;
                let transactionList = [];
                transactionsSnapshot.forEach((doc) => {
                    const transaction = { id: doc.id, ...doc.data() };
                    if (transaction.type !== "deposit") {
                        total += transaction.amount || 0; // Sum up non-deposit amounts
                    }
                    transactionList.push(transaction);
                });
                
                // Sort by date descending
                transactionList.sort((a, b) => {
                    const timeA = a.timestamp ? (a.timestamp.toDate ? a.timestamp.toDate().getTime() : (a.timestamp.seconds ? a.timestamp.seconds * 1000 : new Date(a.timestamp).getTime())) : 0;
                    const timeB = b.timestamp ? (b.timestamp.toDate ? b.timestamp.toDate().getTime() : (b.timestamp.seconds ? b.timestamp.seconds * 1000 : new Date(b.timestamp).getTime())) : 0;
                    return timeB - timeA;
                });
                
                // Keep only top 5 recent transactions
                transactionList = transactionList.slice(0, 5);

                setTotalTransactions(total);
                setTransactions(transactionList);
            } catch (error) {
                console.error("Error fetching transactions data:", error);
            }

            // Fetch Notifications
            try {
                const notificationsRef = collection(db, `users/${uid}/notifications`);
                const notificationsSnapshot = await getDocs(notificationsRef);

                const notificationList = [];
                notificationsSnapshot.forEach((doc) => {
                    notificationList.push(doc.data());
                });

                setNotifications(notificationList);
            } catch (error) {
                console.error("Error fetching notifications data:", error);
            }
        };

        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setUser(user);
                fetchDashboardData(user.uid);
            } else {
                setUser(null);
            }
        });

        return () => unsubscribe();
    }, []);

    return (
        <div className="header-div bg-light1 text-dark2 dark:text-light1 dark:bg-dark1 min-h-screen">
            <Header onToggleMenuClick={toggleMenu} isOpen={isOpen} />
            <div className="flex pt-16">
                <main
                    className={`${isOpen ? "lg:ml-40 " : "lg:ml-11"
                        } transition-all duration-300 space-y-8 ease-in-out w-screen `}
                >
                    <div className="flex justify-between w-9/10 align-middle  bg-light2 dark:bg-dark2 p-2  rounded-sm   lg:w-9/10 mx-auto " >
                        <span className="px-4 text-dark0"> Welcome <span className="text-dark1 dark:text-light1"> {user?.displayName || "New User"} </span>to your Dashboard </span>
                        <div className="flex gap-2">
                            {(userRole === 'user' || userRole === 'regular') && (
                                <button onClick={handleUpgrade} disabled={upgrading} className="btn bg-yellow-500 hover:bg-yellow-600 text-white font-medium border-0">
                                    {upgrading ? 'Upgrading...' : 'Upgrade to Reseller'}
                                </button>
                            )}
                            <button onClick={() => openModal(PaystackPaymentForm)} className="btn">Fund Wallet</button>
                        </div>
                    </div>

                    {/* Wallet, Transaction and product section */}
                    <div className="flex flex-col box-border w-9/10 mx-auto lg:w-9/10 md:mx-auto space-y-6 py-4 bg-light2 dark:bg-dark2 rounded-lg">
                        {/* Wallet and Transaction Info */}
                        <div className="flex flex-col lg:flex-row space-y-4 flex-wrap justify-around items-center">
                            <div className="flex-1 space-y-2 bg-light1 dark:bg-dark1 w-9/10 mx-auto lg:m-4 p-4 rounded-md text-dark0">
                                <div className="flex items-center justify-between">
                                    <span className="flex items-center justify-around">
                                        <FaWallet className="mr-2" /> Wallet Balance:
                                    </span>
                                    <span className="text-primary">₦{walletBalance}</span>
                                </div>
                                <FaChartLine className="h-10 w-10" />
                            </div>
                            <div className="flex-1 space-y-2 bg-light1 dark:bg-dark1 w-9/10 mx-auto lg:m-4 p-4 rounded-md text-dark0">
                                <div className="flex items-center justify-between">
                                    <span className="flex items-center justify-around">
                                        <FaExchangeAlt className="mr-2" /> Total Transactions:
                                    </span>
                                    <span className="text-primary">₦{totalTransactions}</span>
                                </div>
                                <FaChartBar className="w-12 h-12" />
                            </div>
                        </div>
                        {/* product purchase call to action buttons*/}
                        <div className="flex flex-col md:flex-row lg:flex-row flex-wrap space-y-4 justify-around items-center">
                            <div>
                                <Modal isOpen={isModalOpen} onClose={closeModal} contentComponent={activeComponent} />
                            </div>

                            <div onClick={() => openModal(BuyData)} className="lg:flex-1 w-9/10 md:w-4.5/10 flex items-center justify-center border-dark0 border hover:border-primary hover:text-primary rounded-lg cursor-pointer  py-4 lg:m-4">
                                <div className="flex align-middle justify-center w-12 h-12 rounded-full bg-light1 dark:bg-dark1 "><FaWifi className="w-7 h-7 m-2 " /> </div>
                                <div className="flex items-center justify-center text-center bg-light1 dark:bg-dark1 rounded-lg px-8 py-1 -ml-2 ">Buy Data</div>
                            </div>
                            <div onClick={() => openModal(BuyAirtime)} className="lg:flex-1 w-9/10 md:w-4.5/10 flex items-center justify-center border-dark0 border hover:border-primary hover:text-primary rounded-lg cursor-pointer  py-4 lg:m-4">
                                <div className="flex align-middle justify-center w-12 h-12 rounded-full bg-light1 dark:bg-dark1 "><FaPhoneVolume className="w-7 h-7 m-2 " /> </div>
                                <div className="flex items-center justify-center text-center bg-light1 dark:bg-dark1 rounded-lg px-8 py-1 -ml-2 ">Buy Airtime</div>
                            </div>
                            <div onClick={() => openModal(PayElectricityBill)} className="lg:flex-1 w-9/10 md:w-4.5/10 flex items-center justify-center border-dark0 border hover:border-primary hover:text-primary rounded-lg py-4 lg:m-4">
                                <div className="flex align-middle justify-center w-12 h-12 rounded-full bg-light1 dark:bg-dark1 "><FaLightbulb className="w-7 h-7 m-2 " /> </div>
                                <div className="flex items-center justify-center text-center bg-light1 dark:bg-dark1 rounded-lg px-8 py-1 -ml-2 "> Pay Bills</div>
                            </div>
                            <div onClick={() => openModal(CableSubscriptionForm)} className="lg:flex-1 w-9/10 md:w-4.5/10 flex items-center justify-center border-dark0 border hover:border-primary hover:text-primary rounded-lg py-4 lg:m-4">
                                <div className="flex align-middle justify-center w-12 h-12 rounded-full bg-light1 dark:bg-dark1 "><FaTv className="w-7 h-7 m-2 " /> </div>
                                <div className="flex items-center justify-center text-center bg-light1 dark:bg-dark1 rounded-lg px-4 py-1 -ml-2 "> TV Subscription</div>
                            </div>
                        </div>
                    </div>

                    {/* Notifications Section */}
                    <div className="w-9/10 lg:w-9/10 mx-auto space-y-4 bg-light2 dark:bg-dark2 min-h-36 rounded-lg p-4">
                        <span className="flex items-center text-dark0 text-2xl">
                            <FaBell className="m-2" />
                            Notifications:
                        </span>
                        <ul className="space-y-2 ml-1">
                            {notifications.length > 0 ? (
                                notifications.map((notification, index) => (
                                    <li key={index}>{notification.message}</li>
                                ))
                            ) : (
                                <li>No notifications available</li>
                            )}
                        </ul>
                    </div>

                    {/* Transactions Section */}
                    <div className="w-9/10 lg:w-9/10 mx-auto bg-light2 space-y-4 dark:bg-dark2 min-h-36 rounded-lg p-4">
                        <div className="flex justify-between items-center">
                            <span className="flex items-center text-dark0 text-2xl">
                                <FaExchangeAlt className="m-2" />
                                Recent Transactions:
                            </span>
                            <Link to="/account/transactions" className="text-primary hover:underline">
                                See All
                            </Link>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-900/50">
                                    <tr>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">S/N</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type / Network</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ref ID</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {transactions.length > 0 ? (
                                        transactions.map((transaction, index) => (
                                            <tr key={transaction.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{index + 1}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                                                    <span className="font-semibold">{transaction.type || 'N/A'}</span>
                                                    <span className="text-xs ml-2 text-gray-400">
                                                        ({typeof transaction.network === 'string' 
                                                            ? transaction.network 
                                                            : transaction.network?.name 
                                                                ? `${transaction.network.name} ${transaction.network?.code ? `(${transaction.network.code})` : ''}`
                                                                : 'N/A'})
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-white">₦{transaction.amount}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-600 dark:text-gray-400">{transaction.id}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(transaction.response?.status || 'Unknown')}`}>
                                                        {transaction.response?.status || 'Unknown'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{transaction.mobileNumber}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                    {transaction.timestamp ? (transaction.timestamp.toDate ? transaction.timestamp.toDate().toLocaleString() : (transaction.timestamp.seconds ? new Date(transaction.timestamp.seconds * 1000).toLocaleString() : new Date(transaction.timestamp).toLocaleString())) : 'N/A'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 space-x-4">
                                                    <button
                                                        className="text-blue-500 hover:text-blue-700 transition-colors"
                                                        onClick={() => handleViewTransaction(transaction)}
                                                    >
                                                        <FaEye className="w-5 h-5" />
                                                    </button>
                                                    <button
                                                        className="text-red-500 hover:text-red-700 transition-colors"
                                                        onClick={() => handleSingleDelete(transaction.id)}
                                                    >
                                                        <FaTrash className="w-5 h-5" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                                                <div className="flex flex-col items-center">
                                                    <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                    <p className="text-lg font-medium">No transactions available</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className="my-4"></div>
                </main>
            </div>
        </div>
    );
};

export default Dashboard;

